import { useEffect, useState } from 'react';
import axios from 'axios';
import { Package, MapPin, Clock, RefreshCw, Send, Settings, Play, Truck, Calendar, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrackingManagement = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({
    auto_shipping_delay_hours: 36,
    estimated_delivery_days: 14,
    tracking_email_enabled: false
  });
  const [schedulerConfig, setSchedulerConfig] = useState({
    enabled: true,
    hours_between_updates: 12,
    auto_deliver_after_days: 21,
    scheduler_running: false,
    next_run: null,
    recent_runs: []
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [updateForm, setUpdateForm] = useState({
    status: '',
    location: '',
    description: '',
    send_email: false
  });
  const [cronRunning, setCronRunning] = useState(false);
  const [savingScheduler, setSavingScheduler] = useState(false);

  useEffect(() => {
    if (token) {
      fetchOrders();
      fetchConfig();
      fetchSchedulerConfig();
    }
  }, [statusFilter, token]);

  const fetchSchedulerConfig = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/admin/tracking/scheduler`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedulerConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch scheduler config:', error);
    }
  };

  const saveSchedulerConfig = async () => {
    setSavingScheduler(true);
    try {
      await axios.post(
        `${API}/admin/tracking/scheduler`,
        {
          enabled: schedulerConfig.enabled,
          hours_between_updates: schedulerConfig.hours_between_updates,
          auto_deliver_after_days: schedulerConfig.auto_deliver_after_days
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Scheduler settings saved');
      fetchSchedulerConfig();
    } catch (error) {
      toast.error('Failed to save scheduler settings');
    } finally {
      setSavingScheduler(false);
    }
  };

  const runSchedulerNow = async () => {
    setCronRunning(true);
    try {
      await axios.post(
        `${API}/admin/tracking/run-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Tracking update job started');
      // Refresh after a delay
      setTimeout(() => {
        fetchSchedulerConfig();
        fetchOrders();
        setCronRunning(false);
      }, 3000);
    } catch (error) {
      toast.error('Failed to start tracking job');
      setCronRunning(false);
    }
  };

  const fetchOrders = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/tracking/orders`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Fetch orders error:', error);
      toast.error('Failed to fetch tracking orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/tracking/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
    }
  };

  const fetchOrderDetails = async (orderId) => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/tracking/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrderDetails(response.data);
    } catch (error) {
      console.error('Fetch order details error:', error);
      toast.error('Failed to fetch order details');
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
    fetchOrderDetails(order.id);
  };

  const handleAddUpdate = (order) => {
    setSelectedOrder(order);
    setUpdateForm({ status: '', location: '', description: '', send_email: config.tracking_email_enabled });
    setShowUpdateModal(true);
  };

  const submitUpdate = async () => {
    if (!updateForm.status || !updateForm.location || !updateForm.description) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/tracking/${selectedOrder.id}/update`,
        updateForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.email_sent) {
        toast.success('Update added and email sent to customer');
      } else {
        toast.success('Tracking update added successfully');
      }
      
      setShowUpdateModal(false);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to add tracking update');
    }
  };

  const handleRegenerateRoute = async (orderId) => {
    try {
      await axios.post(
        `${API}/tracking/${orderId}/regenerate-route`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Route regenerated successfully');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to regenerate route');
    }
  };

  const handleRunCron = async () => {
    setCronRunning(true);
    try {
      const response = await axios.post(
        `${API}/tracking/run-cron`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Tracking cron executed successfully');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to run tracking cron');
    } finally {
      setCronRunning(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await axios.put(`${API}/tracking/config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuration saved');
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'processing': 'bg-blue-100 text-blue-800',
      'in_transit': 'bg-amber-100 text-amber-800',
      'out_for_delivery': 'bg-purple-100 text-purple-800',
      'delivered': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Automatic Scheduler Card */}
      <Card className="border-blue-200 bg-blue-50/30">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              Automatic Tracking Scheduler
            </span>
            <div className="flex items-center gap-2">
              {schedulerConfig.scheduler_running ? (
                <span className="flex items-center gap-1 text-sm text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  Running
                </span>
              ) : (
                <span className="flex items-center gap-1 text-sm text-red-600">
                  <XCircle className="h-4 w-4" />
                  Stopped
                </span>
              )}
              <Button 
                onClick={runSchedulerNow} 
                disabled={cronRunning}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                data-testid="run-scheduler-now-btn"
              >
                {cronRunning ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                Run Now
              </Button>
            </div>
          </CardTitle>
          <CardDescription>
            Automatically updates order tracking status and sends shipping emails to customers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div className="flex items-center gap-3">
              <Switch
                checked={schedulerConfig.enabled}
                onCheckedChange={(checked) => setSchedulerConfig({ ...schedulerConfig, enabled: checked })}
              />
              <Label>Enable Auto Updates</Label>
            </div>
            <div>
              <Label>Hours Between Updates</Label>
              <Input
                type="number"
                min="1"
                max="72"
                value={schedulerConfig.hours_between_updates}
                onChange={(e) => setSchedulerConfig({ ...schedulerConfig, hours_between_updates: parseInt(e.target.value) || 12 })}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">How often to advance tracking stages</p>
            </div>
            <div>
              <Label>Auto Deliver After (days)</Label>
              <Input
                type="number"
                min="7"
                max="60"
                value={schedulerConfig.auto_deliver_after_days}
                onChange={(e) => setSchedulerConfig({ ...schedulerConfig, auto_deliver_after_days: parseInt(e.target.value) || 21 })}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Maximum days until delivery</p>
            </div>
            <Button onClick={saveSchedulerConfig} disabled={savingScheduler}>
              {savingScheduler ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Scheduler
            </Button>
          </div>
          
          {/* Next Run & Recent Runs */}
          {schedulerConfig.next_run && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Next scheduled run: {new Date(schedulerConfig.next_run).toLocaleString()}
                </span>
                {schedulerConfig.recent_runs?.length > 0 && (
                  <span className="text-gray-500">
                    Last run: {new Date(schedulerConfig.recent_runs[0]?.run_at).toLocaleString()} 
                    ({schedulerConfig.recent_runs[0]?.orders_updated || 0} orders updated)
                  </span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Config Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Manual Tracking Configuration
            </span>
            <Button 
              onClick={handleRunCron} 
              disabled={cronRunning}
              variant="outline"
              className="flex items-center gap-2"
              data-testid="run-cron-btn"
            >
              {cronRunning ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              Run Manual Update
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <Label>Auto-Ship Delay (hours)</Label>
              <Input
                type="number"
                value={config.auto_shipping_delay_hours}
                onChange={(e) => setConfig({ ...config, auto_shipping_delay_hours: parseInt(e.target.value) })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Est. Delivery Days</Label>
              <Input
                type="number"
                value={config.estimated_delivery_days}
                onChange={(e) => setConfig({ ...config, estimated_delivery_days: parseInt(e.target.value) })}
                className="mt-1"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={config.tracking_email_enabled}
                onCheckedChange={(checked) => setConfig({ ...config, tracking_email_enabled: checked })}
              />
              <Label>Email Notifications</Label>
            </div>
            <Button onClick={handleSaveConfig}>Save Config</Button>
          </div>
        </CardContent>
      </Card>

      {/* Orders List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Shipment Tracking ({orders.length})
            </span>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="in_transit">In Transit</SelectItem>
                <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
              </SelectContent>
            </Select>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No shipments found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((order) => (
                <div
                  key={order.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  data-testid={`tracking-order-${order.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                          {order.tracking_number}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(order.maksura_tracking?.status)}`}>
                          {order.maksura_tracking?.status?.replace('_', ' ') || 'Unknown'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        <MapPin className="h-3 w-3 inline mr-1" />
                        {order.shipping_address?.city}, {order.shipping_address?.state}, {order.shipping_address?.country}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Shipped: {order.shipped_at ? new Date(order.shipped_at).toLocaleDateString() : 'N/A'}
                        {' | '}
                        ETA: {order.estimated_delivery ? new Date(order.estimated_delivery).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">
                        {order.maksura_tracking?.updates?.length || 0} updates
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewDetails(order)}
                      >
                        View Route
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAddUpdate(order)}
                      >
                        <Send className="h-4 w-4 mr-1" />
                        Update
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Update Modal */}
      <Dialog open={showUpdateModal} onOpenChange={setShowUpdateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Tracking Update</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Status</Label>
              <Select
                value={updateForm.status}
                onValueChange={(value) => setUpdateForm({ ...updateForm, status: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="picked_up">Picked Up</SelectItem>
                  <SelectItem value="departed_origin">Departed Origin</SelectItem>
                  <SelectItem value="in_transit">In Transit</SelectItem>
                  <SelectItem value="arrived_country">Arrived in Country</SelectItem>
                  <SelectItem value="local_hub">At Local Hub</SelectItem>
                  <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Location</Label>
              <Input
                value={updateForm.location}
                onChange={(e) => setUpdateForm({ ...updateForm, location: e.target.value })}
                placeholder="e.g., Lagos Sorting Center, Nigeria"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={updateForm.description}
                onChange={(e) => setUpdateForm({ ...updateForm, description: e.target.value })}
                placeholder="e.g., Package arrived at local distribution center"
                className="mt-1"
              />
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={updateForm.send_email}
                onCheckedChange={(checked) => setUpdateForm({ ...updateForm, send_email: checked })}
              />
              <Label>Send email notification to customer</Label>
            </div>

            <Button onClick={submitUpdate} className="w-full">
              Add Update
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Tracking Details - {selectedOrder?.tracking_number}
            </DialogTitle>
          </DialogHeader>

          {orderDetails ? (
            <div className="space-y-6 py-4">
              {/* Progress */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Progress</span>
                  <span className="text-sm text-gray-500">
                    {orderDetails.completed_steps} / {orderDetails.total_steps} steps
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-green-600 h-3 rounded-full transition-all"
                    style={{ width: `${orderDetails.progress}%` }}
                  ></div>
                </div>
              </div>

              {/* Route */}
              <Tabs defaultValue="route">
                <TabsList>
                  <TabsTrigger value="route">Full Route</TabsTrigger>
                  <TabsTrigger value="updates">Updates ({orderDetails.order.maksura_tracking?.updates?.length || 0})</TabsTrigger>
                </TabsList>

                <TabsContent value="route" className="mt-4">
                  <div className="space-y-3">
                    {orderDetails.order.maksura_tracking?.route?.map((step, idx) => {
                      const isCompleted = idx < orderDetails.completed_steps;
                      const isCurrent = idx === orderDetails.completed_steps - 1;
                      
                      return (
                        <div
                          key={idx}
                          className={`flex items-start gap-3 p-3 rounded-lg border ${
                            isCompleted ? 'bg-green-50 border-green-200' : 
                            isCurrent ? 'bg-blue-50 border-blue-200' : 
                            'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                            isCompleted ? 'bg-green-600' : 'bg-gray-400'
                          }`}>
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{step.location}, {step.country}</p>
                            <p className="text-sm text-gray-600">{step.description}</p>
                            <p className="text-xs text-gray-400 mt-1">Day {step.day_offset}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>

                <TabsContent value="updates" className="mt-4">
                  <div className="space-y-3">
                    {orderDetails.order.maksura_tracking?.updates?.slice().reverse().map((update, idx) => (
                      <div key={idx} className="border-l-4 border-blue-600 pl-4 py-2">
                        <div className="flex items-center gap-2 mb-1">
                          <MapPin className="h-4 w-4 text-blue-600" />
                          <span className="font-medium">{update.location}</span>
                          {update.added_by === 'admin' && (
                            <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded">Manual</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{update.description}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(update.timestamp).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => handleRegenerateRoute(selectedOrder.id)}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Regenerate Route
                </Button>
                <Button
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleAddUpdate(selectedOrder);
                  }}
                  className="flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  Add Manual Update
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrackingManagement;
